
import { useCallback, useMemo, useState, useEffect } from 'react';
import { loadBlockQuestions, getTotalQuestions } from '../data/lazyQuestions';

const useTestValidation = (testState) => {
  const totalQuestions = useMemo(() => getTotalQuestions(), []);
  const [canGoNextState, setCanGoNextState] = useState(false);

  const getProgress = useMemo(() => {
    let completed = 0;
    
    // Validar respuestas del Bloque A (elecciones múltiples y escalas)
    testState.responses.blockA.forEach(response => {
      if (typeof response === 'object' && response !== null) {
        const characteristicCount = Object.keys(response).length;
        if (characteristicCount === 4) completed++;
      }
    });
    
    // Validar respuestas del Bloque B (rankings)
    testState.responses.blockB.forEach(response => {
      if (typeof response === 'object' && response !== null) {
        const completedRankings = Object.keys(response).length;
        if (completedRankings === 4) completed++;
      }
    });
    
    // Validar respuestas del Bloque C (evaluaciones individuales y comparativas)
    testState.responses.blockC.forEach(response => {
      if (typeof response === 'object' && response !== null && 
          response.individual && response.comparison) {
        const individualComplete = response.individual && 
          Object.keys(response.individual).length === 4;
        const comparisonComplete = response.comparison && 
          Object.keys(response.comparison).length === 2;
        if (individualComplete && comparisonComplete) completed++;
      }
    });
    
    // Validar respuestas del Bloque D (preferencias de imágenes)
    testState.responses.blockD.forEach(response => {
      if (typeof response === 'object' && response !== null) {
        const completedOptions = Object.keys(response).length;
        if (completedOptions === 6) {
          // Verificar que se hayan seleccionado ambas preferencias
          const responseValues = Object.values(response);
          const hasPreferred = responseValues.includes(1); // 1 = Más Preferido
          const hasMostPreferred = responseValues.includes(2); // 2 = Preferido
          if (hasPreferred && hasMostPreferred) completed++;
        }
      }
    });

    return {
      completed,
      total: totalQuestions,
      percentage: Math.round((completed / totalQuestions) * 100)
    };
  }, [testState.responses, totalQuestions]);

  const validateBlockAResponse = useCallback((currentResponse, currentQuestion) => {
    if (typeof currentResponse !== 'object' || currentResponse === null) return false;
    
    if (currentQuestion?.type === 'choice_pair') {
      return currentResponse.more && currentResponse.less && 
             currentResponse.more !== currentResponse.less;
    } 
    else if (currentQuestion?.type === 'multi_scale') {
      if (currentQuestion.characteristics) {
        const expectedCharacteristics = currentQuestion.characteristics.length;
        const answeredCharacteristics = Object.keys(currentResponse).length;
        return answeredCharacteristics === expectedCharacteristics;
      }
    }
    return false;
  }, []);

  const validateBlockBResponse = useCallback((currentResponse) => {
    if (typeof currentResponse !== 'object' || currentResponse === null) return false;
    const completedRankings = Object.keys(currentResponse).length;
    return completedRankings === 4;
  }, []);

  const validateBlockCResponse = useCallback(async (currentResponse) => {
    if (typeof currentResponse !== 'object' || currentResponse === null) {
      return false;
    }
    
    const currentScreen = currentResponse.currentScreen || 'individual';
    
    try {
      const blockQuestions = await loadBlockQuestions('C');
      const question = blockQuestions[0]; // Solo hay una pregunta en el bloque C
      
      if (currentScreen === 'individual') {
        const individualResponses = currentResponse.individual || {};
        const currentSongIndex = currentResponse.currentSongIndex || 0;
        
        // Cambio: validar que hay al menos una respuesta para cualquier canción
        // en lugar de buscar una canción específica por índice
        const hasAnyResponse = Object.keys(individualResponses).length > 0 &&
          Object.values(individualResponses).some(response => response !== undefined);
        
        return hasAnyResponse;
      }
      
      if (currentScreen === 'comparison') {
        const comparisonResponses = currentResponse.comparison || {};
        const currentPairIndex = currentResponse.currentPairIndex || 0;
        
        // Obtener el seed del usuario para recrear el mismo orden aleatorio
        const userSeed = currentResponse.userSeed;
        if (!userSeed || !question.pairs) return false;
        
        // Recrear el mismo shuffle que usa BlockC
        const shuffleArray = (array, seed) => {
          const shuffled = [...array];
          let random = () => {
            const x = Math.sin(seed++) * 10000;
            return x - Math.floor(x);
          };
          
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          return shuffled;
        };
        
        const shuffledPairs = shuffleArray(question.pairs, userSeed + 1000);
        
        if (shuffledPairs && currentPairIndex < shuffledPairs.length) {
          const currentPairId = shuffledPairs[currentPairIndex].id;
          const hasResponse = comparisonResponses[currentPairId] !== undefined;
          return hasResponse;
        }
        return false;
      }
    } catch (error) {
      return false;
    }
    
    return false;
  }, []);

  const validateBlockDResponse = useCallback((currentResponse) => {
    if (typeof currentResponse !== 'object' || currentResponse === null) return false;
    
    const completedOptions = Object.keys(currentResponse).length;
    if (completedOptions !== 6) return false; // Todas las 6 opciones deben estar calificadas
    
    const responseValues = Object.values(currentResponse);
    const hasPreferred = responseValues.includes(1);
    const hasMostPreferred = responseValues.includes(2);
    
    return hasPreferred && hasMostPreferred;
  }, []);

  useEffect(() => {
    const updateCanGoNext = async () => {
      const currentBlockKey = testState.currentBlock;
      const currentQuestionIndex = testState.currentQuestion;
      const blockResponsesKey = `block${currentBlockKey}`;
      const currentResponse = testState.responses[blockResponsesKey][currentQuestionIndex];
      
      let canGo = false;
      
      switch (currentBlockKey) {
        case 'A':
          try {
            const blockQuestions = await loadBlockQuestions(currentBlockKey);
            const currentQuestion = blockQuestions[currentQuestionIndex];
            canGo = validateBlockAResponse(currentResponse, currentQuestion);
          } catch (error) {
            canGo = false;
          }
          break;
        case 'B':
          canGo = validateBlockBResponse(currentResponse);
          break;
        case 'C':
          canGo = await validateBlockCResponse(currentResponse);
          break;
        case 'D':
          canGo = validateBlockDResponse(currentResponse);
          break;
        default:
          canGo = false;
      }
      
      setCanGoNextState(canGo);
    };

    updateCanGoNext();
  }, [testState.currentBlock, testState.currentQuestion, testState.responses, validateBlockAResponse, validateBlockBResponse, validateBlockCResponse, validateBlockDResponse]);

  const canGoNext = useCallback(() => {
    return canGoNextState;
  }, [canGoNextState]);

  const isTestCompleted = useCallback(() => {
    return getProgress.completed === getProgress.total;
  }, [getProgress]);

  return {
    getProgress,
    canGoNext,
    isTestCompleted
  };
};

export default useTestValidation;